import { useState, useMemo } from "react";
import { C, EXERCISE_LIBRARY } from "../constants";
import { Modal, Btn, Badge, iStyle, lblSt } from "./ui";
import { CustomExerciseModal } from "./CustomExerciseModal";

const PLAN_DEFAULTS = { sets: 3, restSecs: 90, repRange: { min: 8, max: 12 }, intensityTechnique: null, supersetGroup: null, myoRepTarget: 3 };

export const PlanBuilder = ({ plan, onSave, onClose, customExercises = [], onAddCustomExercise }) => {
  const [name,      setName]       = useState(plan?.name  || "");
  const [emoji,     setEmoji]      = useState(plan?.emoji || "💪");
  const [exercises, setExercises]  = useState(
    plan?.exercises ? plan.exercises.map(e => ({ ...PLAN_DEFAULTS, ...e })) : []
  );
  const [adding,    setAdding]     = useState(false);
  const [libFilter, setLibFilter]  = useState("");
  const [showCustom, setShowCustom] = useState(false);

  const fullLibrary = useMemo(() => [...EXERCISE_LIBRARY, ...customExercises], [customExercises]);

  const addFromLib = ex => {
    setExercises(prev => [...prev, {
      ...PLAN_DEFAULTS,
      id: Date.now(),
      exerciseId: ex.id || null,
      name: ex.name, muscleGroup: ex.muscleGroup,
      primarySubgroup: ex.p || null, secondarySubgroups: ex.s || [],
    }]);
    setAdding(false); setLibFilter("");
  };

  const handleCustomCreated = (libEntry) => {
    if (onAddCustomExercise) onAddCustomExercise(libEntry);
    addFromLib(libEntry);
    setShowCustom(false);
  };
  const updateEx       = (id, key, val)   => setExercises(prev => prev.map(e => e.id === id ? { ...e, [key]: val } : e));
  const updateRepRange = (id, bound, val) => setExercises(prev => prev.map(e => e.id === id ? { ...e, repRange: { ...e.repRange, [bound]: +val } } : e));
  const removeEx = id => setExercises(prev => prev.filter(e => e.id !== id));
  const moveEx   = (idx, dir) => {
    const arr = [...exercises], swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setExercises(arr);
  };

  const filteredLib = fullLibrary.filter(e => e.name.toLowerCase().includes(libFilter.toLowerCase()));

  return (
    <Modal onClose={onClose} title={plan ? "Edit Plan" : "New Plan"} wide>
      <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
        <div>
          <label style={lblSt}>Emoji</label>
          <input value={emoji} onChange={e => setEmoji(e.target.value)}
            style={{ ...iStyle, width: 60, textAlign: "center", fontSize: 20 }} maxLength={2} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lblSt}>Plan Name</label>
          <input placeholder="e.g. Push Day" value={name} onChange={e => setName(e.target.value)} style={iStyle} />
        </div>
      </div>

      <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
        Exercises ({exercises.length})
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14, maxHeight: 320, overflowY: "auto" }}>
        {exercises.map((ex, idx) => (
          <div key={ex.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{ex.name}</span>
                <Badge>{ex.muscleGroup}</Badge>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => moveEx(idx, -1)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>↑</button>
                <button onClick={() => moveEx(idx,  1)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>↓</button>
                <button onClick={() => removeEx(ex.id)} style={{ background: "none", border: "none", color: C.red,  cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Sets</div>
                <input type="number" value={ex.sets} onChange={e => updateEx(ex.id, "sets", +e.target.value)}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Min Reps</div>
                <input type="number" value={ex.repRange?.min ?? 8} onChange={e => updateRepRange(ex.id, "min", e.target.value)}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Max Reps</div>
                <input type="number" value={ex.repRange?.max ?? 12} onChange={e => updateRepRange(ex.id, "max", e.target.value)}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13 }} />
              </div>
              <div style={{ flex: 1.3 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Rest (s)</div>
                <input type="number" value={ex.restSecs} onChange={e => updateEx(ex.id, "restSecs", +e.target.value)}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13 }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Intensity Technique</div>
                <select
                  value={ex.intensityTechnique || ""}
                  onChange={e => updateEx(ex.id, "intensityTechnique", e.target.value || null)}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13 }}
                >
                  <option value="">None</option>
                  <option value="dropset">Dropset</option>
                  <option value="myo_reps">Myo-Reps</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Superset Group</div>
                <select
                  value={ex.supersetGroup || ""}
                  onChange={e => updateEx(ex.id, "supersetGroup", e.target.value || null)}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13 }}
                >
                  <option value="">None</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              </div>
            </div>
            {ex.intensityTechnique === "myo_reps" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ ...lblSt, fontSize: 9, marginBottom: 3 }}>Mini Set Target</div>
                <input type="number" min="1" max="10"
                  value={ex.myoRepTarget ?? 3}
                  onChange={e => updateEx(ex.id, "myoRepTarget", Math.max(1, +e.target.value || 1))}
                  style={{ ...iStyle, padding: "6px 8px", fontSize: 13, maxWidth: 100 }} />
              </div>
            )}
          </div>
        ))}
        {exercises.length === 0 && (
          <div style={{ color: C.muted, textAlign: "center", padding: "20px 0", fontSize: 13 }}>No exercises yet</div>
        )}
      </div>

      {adding ? (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Add Exercise</div>
          <input placeholder="Search library..." value={libFilter} onChange={e => setLibFilter(e.target.value)}
            style={{ ...iStyle, marginBottom: 8 }} autoFocus />
          <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            {filteredLib.map(ex => (
              <div key={ex.name} onClick={() => addFromLib(ex)} style={{
                padding: "8px 12px", borderRadius: 7, cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13,
              }}>
                <span>{ex.name}</span><Badge>{ex.muscleGroup}</Badge>
              </div>
            ))}
            {filteredLib.length === 0 && (
              <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: "12px 0" }}>
                No exercises match "{libFilter}"
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="outline" onClick={() => setShowCustom(true)} style={{ flex: 1 }}>+ Add Custom Exercise</Btn>
            <Btn onClick={() => { setAdding(false); setLibFilter(""); }} style={{ flex: 1 }}>Cancel</Btn>
          </div>
        </div>
      ) : (
        <Btn variant="outline" onClick={() => setAdding(true)} style={{ width: "100%", marginBottom: 14 }}>+ Add Exercise</Btn>
      )}

      {showCustom && (
        <CustomExerciseModal
          onSave={handleCustomCreated}
          onClose={() => setShowCustom(false)}
        />
      )}

      <Btn variant="primary"
        onClick={() => {
          if (!name || exercises.length === 0) return;
          onSave({ ...plan, id: plan?.id || Date.now(), name, emoji, exercises });
          onClose();
        }}
        style={{ width: "100%", padding: "13px 0", fontSize: 15, opacity: exercises.length === 0 ? 0.4 : 1 }}>
        {exercises.length === 0 ? "Add at least one exercise" : "Save Plan"}
      </Btn>
    </Modal>
  );
};
